import { createApp } from 'vue'
import { createPinia } from 'pinia'
import App from '@/App.vue'
import { loadConfig } from '@/config/runtime'
import { resolveTenant } from '@/config/tenant'
import { i18n, getBrowserLocale, getStoredLocale, setAppLocale } from '@/i18n'
import { createAppRouter } from '@/router'
import { registerTokenGetter } from '@/api/interceptors/auth'
import { registerSessionExpiredHandler } from '@/api/interceptors/errors'
import { useTenantStore } from '@/stores/tenant'
import { useSessionStore } from '@/stores/session'
import { authEnabled, getAuth, initAuth } from '@/auth/oidc'

/* Ordered boot: config -> tenant -> theming/lang -> stores -> i18n -> router.
   Everything the app needs is resolved before mount; components never deal
   with "config not loaded yet" states. */
export async function bootstrap(): Promise<void> {
  const config = await loadConfig()
  const tenant = resolveTenant(window.location.hostname, config)

  document.documentElement.dataset.tenant = tenant.id

  const app = createApp(App)
  app.use(createPinia())

  const tenantStore = useTenantStore()
  tenantStore.setTenant(tenant)

  app.use(i18n)
  /* Locale priority: explicit user choice > browser preference > tenant default */
  const preferred = getStoredLocale() ?? getBrowserLocale()
  setAppLocale(
    preferred && tenant.supportedLocales.includes(preferred) ? preferred : tenant.defaultLocale,
  )

  // OIDC for this tenant (no-op if the tenant has no client id yet).
  initAuth(tenant)
  const sessionStore = useSessionStore()
  registerTokenGetter(() => sessionStore.token)

  if (authEnabled()) {
    // Keep the store in sync with oidc-client-ts: silent (refresh-token)
    // renewals load a fresh user; external logout, an expired access token that
    // could not be renewed, or a failed silent renew all drop the session.
    const auth = getAuth()
    auth.events.addUserLoaded((u) => sessionStore.setUser(u))
    auth.events.addUserUnloaded(() => sessionStore.setUser(null))
    auth.events.addAccessTokenExpired(() => sessionStore.setUser(null))
    auth.events.addSilentRenewError(() => sessionStore.invalidate())
    await sessionStore.init()
  }

  const router = createAppRouter()
  /* Idempotent session-expiry handling: N parallel RPCs failing with
     Unauthenticated trigger ONE re-login, and never while already on the
     callback route. */
  let expiring = false
  registerSessionExpiredHandler(() => {
    const current = router.currentRoute.value
    if (expiring || current.name === 'callback' || !authEnabled()) return
    expiring = true
    sessionStore.invalidate()
    void sessionStore.login(current.fullPath).finally(() => {
      expiring = false
    })
  })

  app.use(router)
  await router.isReady()
  app.mount('#app')
}
